import React, { useEffect, useState } from 'react';
import './AdminSettingsPage.scss';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import Loader from '../../../shared/components/common/loader/Loader';
import StatTile from '../../../shared/components/stat-tile/StatTile';
import {
  getBlockedIps,
  addBlockedIp,
  removeBlockedIp,
  toggleConfiguration,
  getConfiguration,
  BlockedIp,
} from '../../../shared/services/admin/dashboard/AdminDashboardService';
import { AdminConfigurationResponse } from '../../../shared/models/admin/dashboard/AdminConfigurationResponse';
import { format } from 'date-fns';

// Runtime flags (Configuration table) the support team switches from here. Each is read by the
// backend per request, so a toggle takes effect immediately, without a deploy.
const PROTECTION_FLAGS = [
  {
    feature: 'EMAIL_VERIFICATION',
    label: 'Email verification',
    hint: 'New registrations must confirm their e-mail before the panel opens.',
    icon: 'ico--shield-check',
  },
  {
    feature: 'FINGERPRINT_PROTECTION',
    label: 'Fingerprint protection',
    hint: 'A browser that already registered two accounts cannot register a third — signup form, magic link and Google alike.',
    icon: 'ico--eye',
  },
  {
    feature: 'BLOCK_EMAIL_ALIASES',
    label: 'Block e-mail aliases',
    hint: 'Reject registrations with a plus alias in the address (e.g. name+tag@gmail.com).',
    icon: 'ico--close',
  },
  {
    feature: 'AUTO_BAN_DANGEROUS_INPUT',
    label: 'Auto-ban for injected code',
    hint: 'Ban the account when a prompt, project name, user data or invite contains executable markup (an iframe with a src, a script, onerror=). Off: the save goes through and the hit is only logged.',
    icon: 'ico--warning',
  },
] as const;

const AdminSettingsPage: React.FC = () => {
  const [blockedIps, setBlockedIps] = useState<BlockedIp[] | null>(null);
  const [configurations, setConfigurations] = useState<AdminConfigurationResponse[] | null>(null);
  const [savingFeature, setSavingFeature] = useState<string | null>(null);
  const [newIp, setNewIp] = useState('');
  const [newReason, setNewReason] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [addVisible, setAddVisible] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [ips, configs] = await Promise.all([getBlockedIps(), getConfiguration()]);
    setBlockedIps(ips);
    setConfigurations(configs);
  };

  const isEnabled = (feature: string): boolean =>
    configurations?.find((c) => c.feature === feature)?.isEnabled ?? false;

  const handleToggle = async (feature: string) => {
    setSavingFeature(feature);
    try {
      await toggleConfiguration(feature, !isEnabled(feature));
      await loadData();
    } finally {
      setSavingFeature(null);
    }
  };

  // Every flag changes what new registrations go through, live — so no silent flips.
  const confirmToggle = (flag: (typeof PROTECTION_FLAGS)[number]) => {
    const turningOn = !isEnabled(flag.feature);
    confirmDialog({
      message: turningOn
        ? `Turn on "${flag.label}"? ${flag.hint} Applies to new registrations immediately.`
        : `Turn off "${flag.label}"? New registrations will no longer be checked this way.`,
      header: flag.label,
      defaultFocus: 'reject',
      acceptLabel: turningOn ? 'Turn on' : 'Turn off',
      rejectLabel: 'Cancel',
      className: 'dialog-box',
      accept: () => handleToggle(flag.feature),
    });
  };

  const openAddIp = () => {
    setNewIp('');
    setNewReason('');
    setAddError(null);
    setAddVisible(true);
  };

  const handleAddIp = async () => {
    if (!newIp.trim()) {
      setAddError('IP address is required');
      return;
    }
    setAddError(null);
    try {
      await addBlockedIp(newIp.trim(), newReason.trim() || null);
      setAddVisible(false);
      await loadData();
    } catch (err: any) {
      setAddError(err?.response?.data?.error || 'Failed to add IP');
    }
  };

  const handleRemoveIp = async (id: number) => {
    await removeBlockedIp(id);
    await loadData();
  };

  const isLoading = configurations === null || blockedIps === null;

  return (
    <div className="admin-settings">
      <ConfirmDialog className="dialog-box" />
      <div
        className={`stat-tiles admin-settings__section${
          isLoading ? ' admin-settings__section--loading' : ''
        }`}
      >
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            {PROTECTION_FLAGS.map((flag) => (
              <StatTile
                key={flag.feature}
                title={flag.label}
                icon={flag.icon}
                trend={isEnabled(flag.feature) ? 'rising' : 'neutral'}
                value={
                  <span
                    className={`admin-settings__state admin-settings__state--${
                      isEnabled(flag.feature) ? 'on' : 'off'
                    }`}
                  >
                    {isEnabled(flag.feature) ? 'On' : 'Off'}
                  </span>
                }
              />
            ))}
            <StatTile title="Blocked IPs" icon="ico--globe" value={blockedIps.length} />
          </>
        )}
      </div>

      <div className="admin-settings__block">
        <div className="admin-settings__card-head">
          <div className="admin-settings__card-title">Protection</div>
        </div>
        <div className="admin-settings__card">
          {PROTECTION_FLAGS.map((flag) => (
            <div key={flag.feature} className="admin-settings__toggle">
              <InputSwitch
                className="input-switch--sm"
                checked={isEnabled(flag.feature)}
                disabled={isLoading || savingFeature !== null}
                onChange={() => confirmToggle(flag)}
              />
              <div className="admin-settings__toggle-text">
                <div className="admin-settings__toggle-label">{flag.label}</div>
                <div className="admin-settings__toggle-hint">{flag.hint}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-settings__block admin-settings__table">
        <div className="admin-settings__card-head">
          <div className="admin-settings__card-title">Blocked IPs</div>
          <Button type="button" className="btn btn--sm" onClick={openAddIp}>
            <span className="ico--plus ico--xs" />
            Block IP
          </Button>
        </div>

        <DataTable
          value={blockedIps ?? []}
          tableClassName="table"
          className="table-wrapper table-wrapper--stack admin-settings__blocked-ips"
          responsiveLayout="stack"
          tableStyle={{ width: '100%' }}
          stripedRows
          emptyMessage={() => (
            <EmptyPanel
              size="xs"
              textKey="No blocked IPs"
              descriptionKey="Addresses added here cannot register a new account."
            />
          )}
        >
          <Column
            field="ip"
            header="IP Address"
            headerClassName="table__header"
            bodyClassName="table__cell--stack-title"
            style={{ width: '180px' }}
          />
          <Column
            field="reason"
            header="Reason"
            headerClassName="table__header"
            bodyClassName="table__cell--stack-wide"
            body={(row: BlockedIp) => row.reason || '-'}
          />
          <Column
            field="createdAt"
            header="Created"
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '160px' }}
            body={(row: BlockedIp) => {
              try {
                return format(new Date(row.createdAt), 'yyyy-MM-dd HH:mm');
              } catch {
                return row.createdAt;
              }
            }}
          />
          <Column
            header="Actions"
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center table__cell--stack-actions"
            style={{ width: '100px' }}
            body={(row: BlockedIp) => (
              <Button
                type="button"
                className="action-btn action-btn--danger"
                title="Delete"
                onClick={() => handleRemoveIp(row.id)}
              >
                <span className="ico--trash ico--xs" />
              </Button>
            )}
          />
        </DataTable>
      </div>

      <Dialog
        header="Block IP address"
        visible={addVisible}
        onHide={() => setAddVisible(false)}
        className="dialog-box admin-settings__dialog"
        footer={
          <div className="admin-settings__dialog-footer">
            <Button type="button" className="btn btn--sm" onClick={() => void handleAddIp()}>
              <span className="ico--plus ico--xs" />
              Block
            </Button>
          </div>
        }
      >
        <div className="admin-settings__form">
          <label className="admin-settings__field">
            <span>IP address</span>
            <InputText
              value={newIp}
              autoFocus
              onChange={(e) => {
                setNewIp(e.target.value);
                setAddError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && void handleAddIp()}
              placeholder="e.g. 203.0.113.42"
              className={addError ? 'form-box__invalid' : ''}
            />
          </label>
          <label className="admin-settings__field">
            <span>Reason (optional)</span>
            <InputText
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handleAddIp()}
              placeholder="Why this address is blocked"
            />
          </label>
          <div className="admin-settings__dialog-hint">
            Registrations from this address are refused. Existing accounts are not affected.
          </div>
          {addError && <div className="p-error">{addError}</div>}
        </div>
      </Dialog>
    </div>
  );
};

export default AdminSettingsPage;
